// === ./src/frontend/components/FeaturesSection.jsx ===
import React, { useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  alpha,
} from "@mui/material";
import {
  Speed,
  Storage,
  Public,
  Search,
  AttachMoney,
} from "@mui/icons-material";
import { Shield } from "@mui/icons-material";
import { slideInUp } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const FeaturesSection = ({ theme }) => {
  const { lang } = useContext(TranslateContext);

  const features = [
    {
      icon: <Speed />,
      title: lang?.feat1Title || "Blazing Fast Servers",
      color: theme.palette.primary.main,
      desc:
        lang?.feat1Desc ||
        "Our datacenter servers connect to torrent swarms at up to 10 Gbps — your download completes in minutes, not hours.",
    },
    {
      icon: <Shield />,
      title: lang?.feat2Title || "Private & Secure",
      color: theme.palette.info.main,
      desc:
        lang?.feat2Desc ||
        "Your IP is never exposed to the torrent network. All traffic between you and our servers is SSL encrypted.",
    },
    {
      icon: <Storage />,
      title: lang?.feat3Title || "Personal Cloud Storage",
      color: theme.palette.success.main,
      desc:
        lang?.feat3Desc ||
        "Every completed download is stored in your private cloud vault. Access, stream, or re-download anytime.",
    },
    {
      icon: <Public />,
      title: lang?.feat4Title || "Access Anywhere",
      color: theme.palette.warning.main,
      desc:
        lang?.feat4Desc ||
        "Log in from any browser, any device. Your files follow you — desktop, tablet, or mobile.",
    },
    {
      icon: <Search />,
      title: lang?.feat5Title || "Built-in Search",
      color: theme.palette.secondary?.main || theme.palette.primary.main,
      desc:
        lang?.feat5Desc ||
        "Search across multiple torrent indexes directly from the dashboard. No need to visit external sites.",
    },
    {
      icon: <AttachMoney />,
      title: lang?.feat6Title || "Flexible Plans",
      color: theme.palette.error.main,
      desc:
        lang?.feat6Desc ||
        "Start free, scale as you grow. Pay only for the storage and downloads you actually need.",
    },
  ];

  return (
    <Box id="features" sx={{ py: { xs: 10, md: 14 } }}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={8}>
          <Chip
            label={lang?.featChip || "Everything You Need"}
            size="small"
            sx={{
              mb: 2,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{
              fontSize: { xs: "2rem", md: "2.8rem" },
              letterSpacing: "-0.03em",
              mb: 2,
            }}
          >
            {lang?.featTitle || "Powerful Features"}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 500, mx: "auto", lineHeight: 1.7 }}
          >
            {lang?.featSubtitle ||
              "Built for speed, privacy, and convenience. Everything you need to download and manage files from the cloud."}
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {features.map((f, i) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={i}>
              <Box
                sx={{
                  animation: `${slideInUp} 0.6s ease both`,
                  animationDelay: `${i * 0.1}s`,
                }}
              >
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: `1px solid ${alpha(f.color, 0.12)}`,
                    transition: "all 0.3s ease",
                    cursor: "default",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 16px 40px ${alpha(f.color, 0.12)}`,
                      borderColor: alpha(f.color, 0.25),
                      "& .feat-icon": { transform: "scale(1.1) rotate(-5deg)" },
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      className="feat-icon"
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        color: "#fff",
                        mb: 2,
                        background: `linear-gradient(135deg, ${f.color}, ${alpha(f.color, 0.6)})`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 6px 16px ${alpha(f.color, 0.3)}`,
                        transition: "transform 0.3s ease",
                      }}
                    >
                      {React.cloneElement(f.icon, { sx: { fontSize: 22 } })}
                    </Box>
                    <Typography variant="h6" fontWeight={700} mb={1}>
                      {f.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {f.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
